import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Download, Printer, Smartphone } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { track, siteUrl } from "@/lib/api";
import { AnkaraStrip } from "@/components/dm/ui";
import { ShareActions } from "@/components/dm/ShareActions";

export default function ShareApp() {
  const { t } = useApp();
  const ref = useRef(null);
  const navigate = useNavigate();
  const url = siteUrl();
  const link = `${url}/?via=share`;
  const log = (channel) => track("share_click", { meta: { target: "app", channel } });

  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png"); a.download = "diaspomark-qr.png"; a.click(); log("qr_download");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6" data-testid="share-app-page">
      <div className="space-y-1">
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">{t("share_title")}</h1>
        <p className="text-[#6B5E55]">{t("share_sub")}</p>
      </div>
      <div className="print:hidden"><ShareActions link={link} msg={t("share_msg")} target="app" prefix="share-app" /></div>
      <section className="rounded-[28px] overflow-hidden bg-[#1E4D3B] text-white print:shadow-none" id="qr-flyer">
        <AnkaraStrip />
        <div className="p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-6">
          <div ref={ref} className="bg-[#FFFDF9] p-4 rounded-3xl shadow-xl" data-testid="app-qr-code">
            <QRCodeCanvas value={url} size={220} level="H" fgColor="#1E4D3B" bgColor="#FFFDF9" marginSize={1}
              imageSettings={{ src: "/icon-192.png", height: 46, width: 46, excavate: true }} />
          </div>
          <div className="space-y-3 text-center sm:text-left">
            <div className="text-[#F59E0B] font-extrabold text-2xl">Diaspomark</div>
            <p className="text-lg font-bold leading-snug">{t("hero_free")}</p>
            <p className="text-white/80 text-sm">{t("scan_hint")}</p>
            <p data-testid="app-url" className="font-mono text-sm text-[#FDE68A] break-all">{url.replace(/^https?:\/\//, "")}</p>
          </div>
        </div>
        <AnkaraStrip />
      </section>
      <div className="flex flex-wrap gap-3 print:hidden">
        <button data-testid="qr-download" onClick={download} className="press min-h-[52px] px-6 rounded-full bg-[#F59E0B] text-[#291E18] font-extrabold inline-flex items-center gap-2"><Download className="w-5 h-5" />{t("qr_download")}</button>
        <button data-testid="qr-print" onClick={() => { log("print"); navigate("/flyer"); }} className="press min-h-[52px] px-6 rounded-full border-2 border-[#1E4D3B] text-[#1E4D3B] font-extrabold inline-flex items-center gap-2"><Printer className="w-5 h-5" />{t("flyer_open")}</button>
      </div>
      <button data-testid="share-invite-link" onClick={() => navigate("/invite")} className="press w-full text-left rounded-[20px] bg-[#FEF3C7] border-2 border-[#F59E0B]/60 p-4 font-extrabold text-[#291E18] print:hidden">🎁 {t("invite_banner")}</button>
      <section className="rounded-[20px] bg-[#FFFDF9] border border-[#E8E2D9] p-5 flex gap-4 items-start print:hidden">
        <Smartphone className="w-8 h-8 text-[#1E4D3B] shrink-0" />
        <div><div className="font-extrabold">{t("install_title")}</div><p className="text-sm text-[#6B5E55]">{t("install_steps")}</p></div>
      </section>
    </div>
  );
}
